import React, { useEffect, useState, useRef } from 'react';
import {
  FormControlLabel,
  Link,
  Checkbox,
  Box,
  Alert,
  AlertTitle,
  Divider
} from '@mui/material';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { Redirect } from 'react-router';
import ReCAPTCHA from 'react-google-recaptcha';
import { GoogleLogin } from '@react-oauth/google';

import { useSignin } from '@hooks/useSignin';
import config from '@configFile';
import useLocalStorage from '@hooks/useLocalStorage';
import { useGetIsEmailDisposable } from '@hooks/useSecurity';
import { useVerifyRecapture, RecaptchaResponse } from '@hooks/useRecaptcha';
import { useGetUserData } from '@hooks/useGetUserData';
import trackUse from '@utils/trackUse';
import PageContainer from '@components/PageContainer';
import { isValidEmail, isPasswordOkay, isNameProhibited } from '@utils/index';
import { sanitizeEmail } from '@utils/securityUtils';
import { useSignup } from './useSignup';

const Signup = ({
  onlyShowOneButton = false,
  refreshOnLogin = false
}: {
  onlyShowOneButton?: boolean;
  refreshOnLogin?: boolean;
}) => {
  const { mutate: signin, isLoading: isLoggingIn } = useSignin();
  const { mutate: checkDisposableEmail } = useGetIsEmailDisposable();
  const [email, setEmail] = useLocalStorage('form_email', '');
  const recaptcha = useRef<ReCAPTCHA>(null);
  const [emailConfirm, setEmailConfirm] = useState('');
  const [emailConfirmTouched, setEmailConfirmTouched] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [firstNameTouched, setFirstNameTouched] = useState(false);
  const [lastName, setLastName] = useState('');
  const [lastNameTouched, setLastNameTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [hasVerified, setHasVerified] = useState<boolean>(false);
  const [passwordConfirmTouched, setPasswordConfirmTouched] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasLoggedIn, setHasLoggedIn] = useState(false);

  const { mutate: verifyRecaptcha } = useVerifyRecapture();
  const { data: user } = useGetUserData();
  const { mutate: signup, isLoading: isSigningUp } = useSignup();
  const isLoading = isLoggingIn || isSigningUp;
  const finalEmail = `${email.trim()}`;

  useEffect(() => {
    if (emailTouched && finalEmail && isValidEmail(finalEmail)) {
      checkDisposableEmail(
        { email: finalEmail },
        {
          onSuccess: (data) => {
            if (data.data.disposable) {
              setEmailError(true);

              trackUse({
                item: 'disposable-email-attempted',
                value: email,
                type: 'SECURITY'
              });
            } else {
              setEmailError(false);
            }
            setEmailTouched(false);
          }
        }
      );
    }
  }, [email, emailTouched]); // Only re-run when email or emailTouched changes

  const getLastNameHelperText = () => {
    if (
      lastName?.length !== 0 &&
      lastNameTouched &&
      lastName.trim().length < 2
    ) {
      return 'Last name must be at least 2 characters';
    }
    if (lastName?.length === 0 && lastNameTouched) {
      return 'Last name cannot be empty';
    }

    return undefined;
  };

  const getFirstNameHelperText = () => {
    if (
      firstName?.trim()?.length !== 0 &&
      firstNameTouched &&
      firstName.trim().length < 2
    ) {
      return 'First name must be at least 2 characters';
    }
    if (firstName?.length === 0 && firstNameTouched) {
      return 'First name cannot be empty';
    }

    return undefined;
  };

  if (user?.email && !user?.isSuspended) {
    return <Redirect to="/" />;
  }

  const isSubmitButtonEnabled = () => {
    // Quick checks for direct false conditions
    if (!termsChecked || !hasVerified || emailError) return false;
    if (email !== emailConfirm || password !== passwordConfirm) return false;
    if (!isValidEmail(finalEmail)) return false;
    if (firstName.trim().length < 2 || lastName.trim().length < 2) return false;
    if (!isPasswordOkay(password).isOkay) return false;

    // Check for prohibited names
    const lowerFirstName = firstName.toLowerCase();
    const lowerLastName = lastName.toLowerCase();
    if (isNameProhibited(lowerFirstName, lowerLastName)) {
      trackUse({
        item: 'prohibited-name-wise-guy',
        value: `${lowerFirstName} ${lowerLastName}`,
        type: 'SECURITY'
      });
      return false;
    }

    return true;
  };
  const handleTermsCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTermsChecked(event.target.checked);
  };

  const submitSignup = () => {
    signup({
      email: finalEmail,
      password: password.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim()
    });
  };

  const onChange = (value: string) => {
    verifyRecaptcha(value, {
      onSuccess: (data: RecaptchaResponse) => {
        if (data.success) {
          setHasVerified(true);
        } else {
          // eslint-disable-next-line no-alert
          alert('Please verify you are not a robot');
        }
      },
      onError: () => {
        // eslint-disable-next-line no-alert
        alert('Verification failed, please try again');
      }
    });
  };

  let helperText;

  if (
    password.length > 1 &&
    !isPasswordOkay(password).isOkay &&
    passwordTouched
  ) {
    if (!isPasswordOkay(password).passwordLengthOkay) {
      helperText = 'Password should be at least 12 characters';
    }
    if (!isPasswordOkay(password).passwordNumberOkay) {
      helperText = 'Password should contain a number';
    }
  }

  const successFunction = (data: {
    isSuspended: boolean;
    hasAccount: boolean;
  }) => {
    const { isSuspended } = data;
    // refresh the page in half a second
    if (refreshOnLogin) {
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }

    if (isSuspended) {
      console.log('account suspended');
      setHasError(true);
      setHasLoggedIn(true);
    } else {
      console.log('account not suspended');
      setHasLoggedIn(true);
      setHasError(false);
    }
  };

  const signinResponse = {
    onSuccess: successFunction,
    onError: () => {
      console.log('error');
      setHasError(true);
    }
  };

  const displayAccountSuspended = () => {
    if (hasLoggedIn) {
      if (hasError) {
        return (
          <Alert
            severity="error"
            sx={{
              margin: '1rem'
            }}
          >
            Your account has been suspended for using a disposable email
            address. Please contact us at suspensions@jungle.deals if you
            believe this is an error.
          </Alert>
        );
      }

      return (
        <Alert
          severity="success"
          sx={{
            margin: '1rem'
          }}
        >
          You have successfully signed up!
        </Alert>
      );
    }

    return null;
  };

  const renderGoogleSignUp = () => {
    return (
      <Box>
        {hasError ? (
          <Box
            sx={{
              margin: '0 auto 1rem auto',
              maxWidth: '400px'
            }}
          >
            <Alert severity="error">
              {onlyShowOneButton
                ? 'Invalid email or name. Click the link above to register with a different email.'
                : 'Invalid email or name. Use the sign up below with your primary email.'}
            </Alert>
          </Box>
        ) : null}
        {displayAccountSuspended()}
        <Box marginBottom="16px">
          <GoogleLogin
            text="signup_with"
            onSuccess={(credentialResponse: { credential: string }) => {
              signin(
                {
                  ...credentialResponse,
                  isSignUp: true
                },
                signinResponse
              );
            }}
            onError={() => {
              trackUse({
                item: 'login-fail-click',
                value: `login-fail-click`,
                type: 'ERROR'
              });
            }}
            useOneTap
          />
        </Box>
      </Box>
    );
  };

  if (onlyShowOneButton) {
    return renderGoogleSignUp();
  }

  return (
    <PageContainer title="Register" fullWidth isAdmin={!!user?.isAdmin}>
      <Box
        component="form"
        sx={{
          margin: '0 auto',
          textAlign: 'center',
          maxWidth: '400px'
        }}
      >
        {renderGoogleSignUp()}
        <Box marginTop="8px" marginBottom="8px">
          <Divider />
        </Box>

        <Stack spacing={3}>
          <TextField
            data-testid="email-input"
            label="Email"
            aria-label="Email"
            name="email"
            variant="outlined"
            size="small"
            required
            value={email}
            onChange={(e) => {
              setEmail(sanitizeEmail(e.target.value));
            }}
            onBlur={() => setEmailTouched(true)}
            error={emailError}
            helperText={
              emailError
                ? 'Disposable email addresses are not permitted.'
                : undefined
            }
            autoComplete="email"
          />
          <TextField
            data-testid="email-confirm-input"
            label="Email confirm"
            aria-label="Email confirm"
            name="emailConfirm"
            variant="outlined"
            size="small"
            required
            value={emailConfirm}
            onBlur={() => setEmailConfirmTouched(true)}
            onChange={(e) => {
              const value = e.target.value || '';
              setEmailConfirm(value);
            }}
            autoComplete="email"
            error={email !== emailConfirm && emailConfirmTouched}
            helperText={
              email !== emailConfirm && emailConfirmTouched
                ? 'Emails must match'
                : undefined
            }
          />
          <TextField
            data-testid="first-input"
            label="First name"
            aria-label="First name"
            name="firstName"
            variant="outlined"
            size="small"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value.replace(/\./g, ''))}
            autoComplete="given-name"
            onBlur={() => setFirstNameTouched(true)}
            error={firstName?.length !== 0 && firstName.trim().length < 2}
            helperText={getFirstNameHelperText()}
          />
          <TextField
            data-testid="last-input"
            label="Last name"
            aria-label="Last name"
            name="lastName"
            variant="outlined"
            size="small"
            value={lastName}
            required
            onChange={(e) => {
              setLastName(e.target.value.replace(/\./g, ''));
            }}
            autoComplete="family-name"
            onBlur={() => setLastNameTouched(true)}
            error={
              lastName?.length !== 0 &&
              lastNameTouched &&
              lastName.trim().length < 2
            }
            helperText={getLastNameHelperText()}
          />
          {firstName?.length !== 0 &&
          lastName?.length !== 0 &&
          isNameProhibited(firstName.toLowerCase(), lastName.toLowerCase()) ? (
            <Alert severity="error" variant="filled">
              <AlertTitle>Invalid name.</AlertTitle>
              Your IP Address has been flagged for suspicious activity and a
              member of our team has been alerted. Please try again.
            </Alert>
          ) : null}
          <TextField
            data-testid="last-input"
            label="Password"
            aria-label="Password"
            name="password"
            variant="outlined"
            size="small"
            value={password}
            type="password"
            required
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordTouched(true);
            }}
            error={!isPasswordOkay(password).isOkay && passwordTouched}
            helperText={helperText}
          />
          <TextField
            data-testid="last-input"
            label="Password confirm"
            aria-label="Password confirm"
            variant="outlined"
            name="passwordConfirm"
            size="small"
            required
            value={passwordConfirm}
            type="password"
            error={passwordConfirm !== password && passwordConfirmTouched}
            helperText={
              passwordConfirm !== password && passwordConfirmTouched
                ? 'Passwords must match'
                : undefined
            }
            onChange={(e) => {
              setPasswordConfirmTouched(true);
              setPasswordConfirm(e.target.value);
            }}
          />
        </Stack>
        <br />
        <ReCAPTCHA
          ref={recaptcha}
          sitekey={config.RECAPTCHA_SITE_KEY}
          onChange={onChange}
        />
        <br />
        <FormControlLabel
          sx={{ mb: '12px' }}
          control={
            <Checkbox
              checked={termsChecked}
              onChange={handleTermsCheckboxChange}
              name="acceptTerms"
              color="primary"
            />
          }
          label={
            <span>
              I have read and I accept the{' '}
              <Link href="/privacy-policy" target="_blank" rel="noopener">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link href="/terms" target="_blank" rel="noopener">
                Terms of Service
              </Link>
              .
            </span>
          }
        />
        <br />
        <Button
          sx={{
            marginTop: '10px'
          }}
          onClick={submitSignup}
          variant="contained"
          disabled={!isSubmitButtonEnabled() || isLoading}
        >
          {isLoading ? 'Loading...' : 'Sign me up!'}
        </Button>
      </Box>
    </PageContainer>
  );
};

export default Signup;
